import React, { useState } from 'react';
import { ArrowLeft, Phone, Lock, Loader2, LogIn } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import logoUrl from '../logo.svg';
import { IndustrialSkyline } from '../components/IndustrialSkyline';
import Seo from '../components/Seo';

export default function Login() {
  const [step, setStep] = useState<'phone' | 'password'>('phone');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phone.length < 10) {
      setError('Please enter a valid 10-digit number');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Rate limit check
      const { data: isLimited } = await supabase.rpc('is_rate_limited', {
        p_action: 'jobisa_web_login',
        p_identifier: phone,
      });

      if (isLimited) {
        setError('Too many login attempts. Please try again later.');
        setLoading(false);
        return;
      }

      // Check if user exists (mocking the Recruiter App logic)
      const { data, error } = await supabase
        .from('recruiters')
        .select('id')
        .eq('phone', phone)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setStep('password');
      } else {
        setError('No recruiter account found with this number. Please register on the mobile app first.');
      }
    } catch (err: any) {
      setError(err.message || 'Error checking account');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await supabase.rpc('log_rate_limit_attempt', {
        p_action: 'jobisa_web_login',
        p_identifier: phone,
      });

      const email = `${phone}@karya.com`;
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password: password,
      });

      if (error) throw error;

      navigate('/app/dashboard');
    } catch (err: any) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-jobisa-navy flex items-center justify-center p-6 relative overflow-hidden">
      <Seo
        title="Recruiter Login"
        description="Sign in to the Jobisa recruiter portal to manage campaigns, applicants, wallet, messages, and hiring workflow."
        path="/login"
        noindex={true}
      />
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-blue-900/20 blur-[120px]"></div>
        <div className="absolute top-[20%] -right-[10%] w-[40%] h-[40%] rounded-full bg-jobisa-yellow/10 blur-[100px]"></div>
      </div>

      {/* Industrial Skyline */}
      <IndustrialSkyline
        className="absolute bottom-0 left-0 w-full h-40 pointer-events-none z-[1]"
        color="#ffffff"
      />

      <div className="w-full max-w-md z-10">
        <div className="glass-card-dark rounded-3xl p-8 backdrop-blur-xl border border-white/10 shadow-2xl">
          <div className="flex flex-col items-center mb-8">
            <div className="bg-white p-3 rounded-2xl shadow-xl shadow-black/20 mb-5 w-20 h-20 flex flex-col items-center justify-center">
              <img src={logoUrl} alt="Jobisa" className="w-14 h-14 object-contain" />
            </div>
            <h1 className="text-3xl font-bold text-white font-display text-center">Recruiter Portal</h1>
            <p className="text-gray-400 text-center mt-2">
              {step === 'phone' ? 'Enter your registered mobile number' : 'Enter your password to access dashboard'}
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 text-sm px-4 py-3 rounded-xl mb-6">
              {error}
            </div>
          )}

          {step === 'phone' ? (
            <form onSubmit={handleNext}>
              <div className="mb-6">
                <label className="block text-gray-300 text-sm font-semibold mb-2 ml-1 uppercase tracking-wide">
                  Phone Number
                </label>
                <div className="flex items-center bg-black/20 border border-white/10 rounded-2xl focus-within:border-jobisa-yellow focus-within:bg-black/40 transition-all overflow-hidden">
                  <div className="pl-4 pr-3 text-gray-400 border-r border-white/10 py-4 flex items-center">
                    <Phone size={18} className="mr-2" />
                    <span>+91</span>
                  </div>
                  <input
                    type="tel"
                    className="flex-1 bg-transparent text-white p-4 outline-none w-full"
                    placeholder="9876543210"
                    maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || phone.length < 10}
                className="w-full btn-primary py-4 rounded-2xl font-bold flex justify-center items-center gap-2 text-lg disabled:opacity-50"
              >
                {loading ? <Loader2 className="animate-spin" /> : 'Continue'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin}>
              <div className="mb-6">
                <label className="block text-gray-300 text-sm font-semibold mb-2 ml-1 uppercase tracking-wide">
                  Password
                </label>
                <div className="flex items-center bg-black/20 border border-white/10 rounded-2xl focus-within:border-jobisa-yellow focus-within:bg-black/40 transition-all overflow-hidden">
                  <div className="pl-4 text-gray-400 py-4">
                    <Lock size={18} />
                  </div>
                  <input
                    type="password"
                    className="flex-1 bg-transparent text-white p-4 outline-none w-full tracking-wider font-mono text-lg"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !password}
                className="w-full btn-primary py-4 rounded-2xl font-bold flex justify-center items-center gap-2 text-lg disabled:opacity-50"
              >
                {loading ? <Loader2 className="animate-spin" /> : (
                  <>
                    <LogIn size={20} />
                    Login to Dashboard
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => { setStep('phone'); setError(''); setPassword(''); }}
                className="w-full mt-4 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={16} className="mr-2" />
                Change number
              </button>
            </form>
          )}

          <div className="mt-8 text-center" style={{ paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <a href="/" className="text-jobisa-yellow hover:text-white transition-colors text-sm font-medium">
              &larr; Back to Jobisa Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
